import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Divider,
  Paper,
  Avatar
} from '@mui/material';
import { Delete, Warning, Category as DefaultCategoryIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import SuccessDialog from './SuccessDialog';

// Import all MUI icons for dynamic rendering
import * as MuiIcons from '@mui/icons-material';

const DeleteDialog = ({ open, onClose, category, onDelete }) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const MotionBox = motion.div;

  if (!category) return null;

  // Get icon component by name
  const getIconByName = (iconName) => {
    if (!iconName) return <DefaultCategoryIcon />;
    
    // Map of friendly names to MUI icon component names
    const iconMap = {
      'Fastfood': 'Fastfood',
      'Drinks': 'LocalDrink',
      'Dessert': 'Cake',
      'Coffee': 'Coffee',
      'Menu': 'RestaurantMenu',
      'Bar': 'LocalBar',
      'Tea': 'EmojiFoodBeverage',
      'Ice Cream': 'Icecream',
      'Pizza': 'LocalPizza',
      'Dining': 'LocalDining',
      'Ramen': 'RamenDining',
      'Sushi': 'SetMeal',
      'Lunch': 'LunchDining',
      'Restaurant': 'Restaurant',
      'Wine': 'Liquor',
      'Soup': 'SoupKitchen',
      'Breakfast': 'BreakfastDining',
      'Bakery': 'BakeryDining',
      'Dinner': 'DinnerDining',
      'Egg': 'EggAlt',
      'Kebab': 'KebabDining',
      'Tapas': 'SportsBar'
    };
    
    // Get the component name from our map
    const componentName = iconMap[iconName];
    
    // Try to get the icon from MUI icons
    if (componentName && MuiIcons[componentName]) {
      const IconComponent = MuiIcons[componentName];
      return <IconComponent />;
    }
    
    // Fallback for icons that don't exist
    return <DefaultCategoryIcon />;
  };

  const selectedIcon = getIconByName(category.icon);

  const handleDelete = async () => {
    if (!category || !onDelete) return;
    
    setIsDeleting(true);
    try {
      await onDelete(category.category_id);
      setSuccessDialogOpen(true);
    } catch (error) {
      console.error('Error deleting category:', error);
      // Error handling would be implemented here
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { borderRadius: 2, maxWidth: '500px' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Warning color="error" sx={{ mr: 1 }} />
            <Typography variant="h6">{t('category.deleteTitle')}</Typography>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent>
          <MotionBox
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Typography gutterBottom>
              {t('category.deleteConfirm')}
            </Typography>
            
            <Box 
              component={Paper}
              sx={{ 
                p: 2, 
                my: 2, 
                border: '1px solid', 
                borderColor: 'error.light', 
                borderRadius: 2,
                bgcolor: 'error.lighter',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Avatar 
                sx={{ 
                  bgcolor: category.colorValue || 'error.main',
                }}
              >
                {selectedIcon}
              </Avatar>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {category.name}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  {t('category.itemsCount', { count: category.itemCount || 0 })}
                </Typography>
              </Box>
              
              {category.image && (
                <Box
                  component="img"
                  src={category.image}
                  alt={category.name}
                  sx={{
                    width: 60,
                    height: 60,
                    objectFit: 'cover',
                    borderRadius: 1
                  }}
                />
              )}
            </Box>
            
            <Typography color="error.main" variant="body2" sx={{ fontWeight: 500 }}>
              {t('category.deleteWarning')}
            </Typography>
            
            {category.itemCount > 0 && (
              <Typography color="error.main" variant="body2" sx={{ mt: 2, fontWeight: 700 }}>
                {t('category.deleteItemsWarning', { count: category.itemCount })}
              </Typography>
            )}
          </MotionBox>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={isDeleting}
          >
            {t('common.cancel')}
          </Button>
          
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <Delete />}
            disabled={isDeleting}
          >
            {isDeleting ? t('category.deleting') : t('category.deleteButton')}
          </Button>
        </DialogActions>
      </Dialog>

      <SuccessDialog 
        open={successDialogOpen} 
        onClose={handleSuccessDialogClose} 
        message={t('category.deleteSuccess')} 
      />
    </>
  );
};

export default DeleteDialog;